"use client";

import { Check } from "lucide-react";
import Link from "next/link";
import { Button } from "@/app/(public)/shared/ui/Button";

interface PricingCardProps {
  className?: string;
  title: string;
  subtitle: string;
  serviceCharge?: string;
  othersIncluded?: string;
  features: string[];
  demoButton?: boolean;
  demoText?: string;
}

const PricingCard = ({
  className = "",
  title,
  subtitle,
  serviceCharge,
  othersIncluded,
  features,
  demoButton,
  demoText,
}: PricingCardProps) => {
  return (
    <div
      className={`bg-white rounded-2xl border border-landing-border-primary flex flex-col justify-between px-4 sm:px-8 py-6 sm:py-10 ${className}`}
    >
      <div className="flex flex-col gap-2 pl-0 sm:pl-2">
        <h3 className="text-tag-lg text-landing-secondary-sand font-semibold mb-1 text-left">{title}</h3>
        <h2 className="text-h3 text-landing-primary-sand font-bold mb-2 text-left">{subtitle}</h2>
        {serviceCharge && <p className="text-body text-landing-secondary-sand mb-1 text-left">{serviceCharge}</p>}
        {othersIncluded && (
          <p className="text-body font-semibold text-landing-primary-sand mb-2 text-left">{othersIncluded}</p>
        )}
        <ul className="mt-2 space-y-2 w-full">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start gap-2 text-body text-landing-secondary-sand text-left">
              <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 shrink-0 mt-0.5" />
              <span className="text-sm sm:text-base">{feature}</span>
            </li>
          ))}
        </ul>
        {title === "Explorer" && (
          <p className="text-body-sm text-landing-secondary-sand mt-3 text-left">$250 refundable deposit</p>
        )}
      </div>
      <div className="mt-6 sm:mt-8">
        <Link href="/create">
          <Button variant="primary" size="md" className="w-full sm:w-auto">
            {demoText ? demoText : "Try our platform"}
          </Button>
        </Link>
      </div>
    </div>
  );
};

const PricingCards = () => {
  const basicFeatures = [
    "1 city search",
    "5 RFPs",
    "AI-powered event planning assistant",
    "Negotiated rates and concessions",
    "Sign contracts and run payments",
  ];
  const explorerFeatures = [
    "3-4 city searches",
    "10 RFPs per city",
    "5 hotel proposals per city",
    "Export to CSV",
    "Shared portal",
    "Dedicated Account Manager",
    "Shared Slack support",
    "Group flight booking assistance",
    "Access agency rates and discounts through IATA accreditation",
    "Get local insights from contractors and Convention & Visitors Bureaus",
    "Full payment handling with hotels and vendors per contract terms",
  ];
  const enterpriseFeatures = [
    "Unlimited city searches",
    "Unlimited RFPs",
    "Personalized venue options",
    "24/7 365-day support",
    "Venue sourcing for unlimited events (up to $800K budget)",
    "Contract negotiation for accommodations, meeting rooms, AV & concessions",
    "Unlimited organizers on platform",
    "Flight analysis for multiple cities",
    "Access to new custom features",
    "Custom feature requests",
    "Flight bookings for events",
    "Monthly 30-min planning consultation",
    "Attendee travel management platform",
  ];

  return (
    <>
      <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-8 justify-center items-stretch pt-8 sm:pt-12 px-4 sm:px-0">
        <PricingCard
          title="Basic"
          subtitle="Start for Free"
          serviceCharge="No service charge"
          features={basicFeatures}
          demoText="Get Started"
        />
        <PricingCard
          title="Explorer"
          subtitle="Book a Demo"
          serviceCharge="10% service charge"
          othersIncluded="Everything in basic"
          features={explorerFeatures}
        />
        <PricingCard
          title="Enterprise"
          subtitle="Book a Demo"
          serviceCharge="For High Volume Teams"
          othersIncluded="Customized to your needs"
          features={enterpriseFeatures}
        />
      </div>
    </>
  );
};

export default PricingCards;
